import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit2, FiSearch } from 'react-icons/fi';
import { getProducts, createProduct, updateProduct, setRewardMapping } from '../services/api';

const Products: React.FC = () => {
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [form, setForm] = useState({
    productName: '', sku: '', description: '', isActive: true,
    labourerCash: 0, dealerPoints: 0, contractorPoints: 0
  });

  useEffect(() => { loadProducts(); }, [page]);

  const loadProducts = async () => {
    try {
      const res = await getProducts(page, 20, search);
      setProducts(res.data.data);
      setTotal(res.data.totalCount);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleSearch = () => { setPage(1); loadProducts(); };

  const openCreate = () => {
    setEditing(null);
    setForm({ productName: '', sku: '', description: '', isActive: true, labourerCash: 0, dealerPoints: 0, contractorPoints: 0 });
    setShowModal(true);
  };

  const openEdit = (p: any) => {
    setEditing(p);
    setForm({
      productName: p.productName, sku: p.sku || '', description: p.description || '',
      isActive: p.isActive, labourerCash: p.labourerCash, dealerPoints: p.dealerPoints, contractorPoints: p.contractorPoints
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      if (editing) {
        await updateProduct(editing.productId, form);
        await setRewardMapping({
          productId: editing.productId,
          labourerCash: form.labourerCash, dealerPoints: form.dealerPoints, contractorPoints: form.contractorPoints
        });
        toast.success('Product updated');
      } else {
        const res = await createProduct(form);
        const pid = res.data.data.productId;
        await setRewardMapping({
          productId: pid,
          labourerCash: form.labourerCash, dealerPoints: form.dealerPoints, contractorPoints: form.contractorPoints
        });
        toast.success('Product created');
      }
      setShowModal(false);
      loadProducts();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to save');
    }
  };

  const totalPages = Math.ceil(total / 20);

  return (
    <div>
      <div className="flex-between mb-20">
        <div className="search-bar">
          <input className="form-control" placeholder="Search products..." value={search}
            onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSearch()} />
          <button className="btn btn-outline" onClick={handleSearch}><FiSearch /></button>
        </div>
        <button className="btn btn-primary" onClick={openCreate}><FiPlus /> Add Product</button>
      </div>

      <div className="card">
        {loading ? <div className="loading">Loading...</div> : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>ID</th><th>Product Name</th><th>SKU</th>
                  <th>Labourer (₹)</th><th>Dealer (pts)</th><th>Contractor (pts)</th>
                  <th>Status</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.productId}>
                    <td>{p.productId}</td>
                    <td><strong>{p.productName}</strong></td>
                    <td>{p.sku || '-'}</td>
                    <td>₹{p.labourerCash}</td>
                    <td>{p.dealerPoints}</td>
                    <td>{p.contractorPoints}</td>
                    <td><span className={`badge ${p.isActive ? 'badge-success' : 'badge-danger'}`}>{p.isActive ? 'Active' : 'Inactive'}</span></td>
                    <td><button className="btn btn-sm btn-outline" onClick={() => openEdit(p)}><FiEdit2 /> Edit</button></td>
                  </tr>
                ))}
                {products.length === 0 && <tr><td colSpan={8} className="text-center">No products found</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="pagination">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)}>Prev</button>
            <span>Page {page} of {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</button>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editing ? 'Edit Product' : 'Add Product'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>

            <div className="form-group">
              <label>Product Name *</label>
              <input className="form-control" value={form.productName}
                onChange={(e) => setForm({ ...form, productName: e.target.value })} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>SKU</label>
                <input className="form-control" value={form.sku}
                  onChange={(e) => setForm({ ...form, sku: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Status</label>
                <select className="form-control" value={form.isActive ? 'true' : 'false'}
                  onChange={(e) => setForm({ ...form, isActive: e.target.value === 'true' })}>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea className="form-control" rows={3} value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>

            <h4 style={{ margin: '20px 0 10px', fontSize: 15 }}>Reward Mapping</h4>
            <div className="form-row">
              <div className="form-group">
                <label>Labourer Cash (₹)</label>
                <input className="form-control" type="number" value={form.labourerCash}
                  onChange={(e) => setForm({ ...form, labourerCash: parseFloat(e.target.value) || 0 })} />
              </div>
              <div className="form-group">
                <label>Dealer Points</label>
                <input className="form-control" type="number" value={form.dealerPoints}
                  onChange={(e) => setForm({ ...form, dealerPoints: parseInt(e.target.value) || 0 })} />
              </div>
              <div className="form-group">
                <label>Contractor Points</label>
                <input className="form-control" type="number" value={form.contractorPoints}
                  onChange={(e) => setForm({ ...form, contractorPoints: parseInt(e.target.value) || 0 })} />
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave}>
                {editing ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;
